import { Tags } from "@babylonjs/core/Misc/tags";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";

const toSpaces = (s) => (s && s.split("$").join(" ")) || s;

const coerceValue = (raw) => {
    if (raw == null) return raw;
    const v = String(raw).trim();
    if (/^(true|false)$/i.test(v)) return /^true$/i.test(v);
    if (/^-?\d+(\.\d+)?$/.test(v)) return Number(v);
    return toSpaces(v);
};

const addTag = (mesh, tag) => {
    if (!tag) return;
    Tags.AddTagsTo(mesh, tag);
};

const parseKVPair = (piece) => {
    const idx = piece.indexOf(":");
    if (idx === -1) {
        return { key: piece.trim(), value: true };
    }
    const key = piece.slice(0, idx).trim();
    const raw = piece.slice(idx + 1).trim();

    return { key, value: coerceValue(raw) };
};

const splitPrefixTokens = (prefix) => prefix.trim().split(/\s+/).filter(Boolean);

const splitUnderscoreToken = (token) => token.split("_").filter(Boolean);

const isRoofSheetMaterialSkylight = (mesh) => {
    const ac = mesh?.material?.ambientColor;
    return ac && ac.r === 1 && ac.g === 1 && ac.b === 1;
};

const isGutterFace = (mesh) => {
    const url = mesh?.material?.ambientTexture?.url || "";
    return url.includes("gutter_d.jpg");
};

const normalizeSelType = (sel) => {
    if (!sel) return sel;

    if (sel === "sheed_end") return "sheet_end";
    if (sel === "barge") return "barge_cap";
    return sel;
};

function buildBaseData() {
    return {
        areaNumber: -1,
        areaIndex: -1,
        ref: "",
        lengthQQ: 0,
        babylon: true,
        sel_type: undefined,
        typeQQ: undefined,
    };
}

function applyPrefixTokens(data, tokens) {
    for (const token of tokens) {
        const [head, ...rest] = splitUnderscoreToken(token);
        const tail = rest.join("_");

        switch (head) {
            case "len":
                if (rest[0] != null) data.lengthQQ = Number(rest[0]);
                break;

            case "area": {
                const n = rest[0] != null ? Number(rest[0]) : 1;
                data.areaNumber = Number.isFinite(n) ? n : 1;
                data.areaIndex = data.areaNumber - 1;
                break;
            }

            case "sheet":
                data.sel_type = "sheet";
                break;

            case "slat":
                if (rest[0] === "post") data.sel_type = "slat_post";
                break;

            case "beam":
                data.sel_type = "beam";
                if (rest[0]) data.beam_name = rest[0];
                break;

            case "beam4":
            case "beam3":
            case "beam2":
            case "beam1":
                data.typeQQ = head;
                if (head === "beam2") {
                    // LeftBeam/RightBeam will also set flags, but this keeps behavior
                }
                break;

            case "LeftBeam":
                data.LeftBeam = 1;
                data.typeQQ = "beam2";
                break;

            case "RightBeam":
                data.RightBeam = 1;
                data.typeQQ = "beam2";
                break;

            case "attachment":
                data.sel_type = "attachment";
                break;

            case "attachment_roof":
                data.sel_type = "attachment_roof";
                break;

            case "attach":
            case "attach_fascia":
                data.typeQQ = "attachment_fascia";
                break;

            case "attach_gutter":
                data.typeQQ = "attachment_gutter";
                break;

            case "gutter":
                data.sel_type = "gutter";
                break;

            case "barge":
            case "barge_cap":
                data.sel_type = "barge_cap";
                break;

            case "ridge":
                data.sel_type = "ridge";
                break;

            case "post":
                data.sel_type = "post";
                break;

            case "post_footing":
                data.sel_type = "post_footing";
                break;

            case "downpipe":
                data.sel_type = "downpipe";
                break;

            case "slat_panel":
                data.sel_type = "slat_panel";
                break;

            case "infill": {
                const maybe = rest[1];
                if (maybe === "1") data.sel_type = "infill_1";
                else if (maybe === "2") data.sel_type = "infill_2";
                else data.sel_type = "infill_1";
                break;
            }

            default: {
                data[head] = rest.length ? toSpaces(tail) : true;
            }
        }
    }
}

function applyObjectDataPairs(data, kvPairs) {
    for (const piece of kvPairs) {
        if (!piece) continue;
        const { key, value } = parseKVPair(piece);

        if (!key) continue;

        if (key === "len") {
            if (value != null) data.lengthQQ = Number(value);
            continue;
        }

        if (key === "area") {
            const n = value != null ? Number(value) : 1;
            data.areaNumber = Number.isFinite(n) ? n : 1;
            data.areaIndex = data.areaNumber - 1;
            continue;
        }

        if (key === "sel_type") {
            if (typeof value === "string" && value.length > 0) {
                data.sel_type = value;
            }
            continue;
        }

        if (key === "type") {
            data.typeQQ = value;
            continue;
        }

        if (key === "name") {
            data.nameQQ = value;
            continue;
        }

        if (key === "underside") {
            data.is_underside = true;
            continue;
        }

        data[key] = value;
    }

    data.sel_type = normalizeSelType(data.sel_type);
}

function deriveFlags(mesh, data) {
    const sel = data.sel_type || "";

    if (["attachment_fascia", "attachment_gutter"].includes(data.typeQQ)) {
        data.sel_type = data.typeQQ;
    }

    data.is_downlight = sel === "downlight";
    data.is_downlight_flashing = sel === "downlight_flashing";
    data.is_fan = sel === "fan";
    data.is_beam = sel.startsWith?.("beam") || sel === "beam";
    data.is_roofsheet = ["sheet", "skylight"].includes(sel);
    data.is_roofsheet_end = ["sheet_end", "skylight_end"].includes(sel);
    data.is_roofsheet_side = ["sheet_side", "skylight_side"].includes(sel);
    data.is_skylight = sel === "skylight";
    data.is_slat = sel === "slat_panel";
    data.is_brace = sel === "brace";
    data.is_backchannel = sel === "back_channel";
    data.is_attachment = sel === "attachment";
    data.is_attachment_roof = sel === "attachment_roof";
    data.is_gutter = sel === "gutter";
    data.is_barge = sel === "barge_cap";
    data.is_ridge = sel === "ridge";
    data.is_post = sel === "post";
    data.is_footing = sel === "post_footing";
    data.is_downpipe = sel === "downpipe";
    data.is_barge_cap = sel === "barge";

    if (!data.is_skylight && data.is_roofsheet && isRoofSheetMaterialSkylight(mesh)) {
        data.sel_type = "skylight";
        data.is_skylight = true;
    }

    data.is_gutter_face = data.is_gutter && isGutterFace(mesh);

    if (data.ref2 && (String(data.ref2).startsWith("CB") || String(data.ref2).startsWith("CF"))) {
        data.is_cutout = true;
    }

    if (data.flashing == 1 && !data.sel_type) data.sel_type = "flashing";
    if (data.riser && !data.sel_type) data.sel_type = "riser_bracket";

    if (data.is_attachment || data.is_attachment_roof) {
        if (data.ref === "unknown" && typeof data.ref2 === "string") {
            if (data.ref2.startsWith("CBR")) data.ref = "BackRight-Attachment";
            if (data.ref2.startsWith("CBM")) data.ref = "BackMid-Attachment";
            if (data.ref2.startsWith("CBL")) data.ref = "BackLeft-Attachment";
            if (data.ref2.startsWith("CFR")) data.ref = "FrontRight-Attachment";
            if (data.ref2.startsWith("CFL")) data.ref = "FrontLeft-Attachment";
        }
    }

    if (data.is_downpipe && !data.ref) data.ref = "Auto";
}

function addDerivedTags(mesh, data) {
    addTag(mesh, data.sel_type);
    if (data.typeQQ) addTag(mesh, data.typeQQ);

    if (data.is_underside) {
        if (data.sel_type === "infill_1") addTag(mesh, "infill_1_underside");
        else if (data.sel_type === "infill_2") addTag(mesh, "infill_2_underside");
        else addTag(mesh, "underside");
    }

    if (data.is_gutter_face) addTag(mesh, "gutter_face");
}

function maybeFlipInfillNormals(mesh, data) {
    if (data.sel_type !== "infill_1") return;
    const normals = mesh.getVerticesData?.(VertexBuffer.NormalKind);
    if (!normals || !normals.length) return;
    if (normals[0] > 0) {
        const flipped = normals.map((n) => -n);
        mesh.setVerticesData(VertexBuffer.NormalKind, flipped);
        mesh.updateVerticesData(VertexBuffer.NormalKind, flipped);
    }
}

export function applyMeshIdData(mesh) {
    const id = String(mesh?.id ?? "");
    const idx = id.indexOf("objectdata");

    const data = buildBaseData();

    if (idx > -1) {
        const prefix = id.slice(0, idx).trim();
        const suffix = id
            .slice(idx)
            .replace(/^objectdata,?/, "")
            .trim();

        const tokens = splitPrefixTokens(prefix);
        applyPrefixTokens(data, tokens);

        const kvPieces = suffix.length ? suffix.split(",") : [];
        applyObjectDataPairs(data, kvPieces);
    } else {
        const tokens = splitPrefixTokens(id);
        applyPrefixTokens(data, tokens);
    }

    deriveFlags(mesh, data);
    addDerivedTags(mesh, data);
    maybeFlipInfillNormals(mesh, data);

    mesh.data = data;
    return data;
}
